import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { useSocketEvent } from '../hooks/useSocket';
import { SocketEvents } from '../constants/socket-events';
import { appearanceService, type AppearanceView } from '../services/appearance.service';
import {
  applyAppearance,
  ensureLookFont,
  readCached,
  resolveTheme,
  writeCached,
  type Look,
  type ThemePref,
} from '../lib/appearance';
import type { Tenant } from '../types/dashboard';

/**
 * Applies the look (admins' choice, from messaging-gateway) and the theme (this person's choice, this browser) to
 * <html>, and keeps both current: the look is refetched when the workspace in the URL changes and when the gateway
 * broadcasts `appearance:changed`; `auto` follows the device's light/dark setting live. Rules: `lib/appearance`.
 */
interface AppearanceContextValue {
  look: Look;
  themePref: ThemePref;
  setThemePref: (pref: ThemePref) => void;
  /** Null until the first fetch answers (the cached look is shown meanwhile). */
  view: AppearanceView | null;
  /** The workspace whose look is in force, when the URL names one. */
  tenant: Tenant | null;
  setSystemLook: (look: Look) => Promise<void>;
  setTenantLook: (look: Look | null) => Promise<void>;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const darkQuery = () => window.matchMedia?.('(prefers-color-scheme: dark)');

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const { apiFetch, token } = useAuth();
  const { tenants } = useTenant();
  const location = useLocation();

  const cached = useMemo(() => readCached(storage()), []);
  const [themePref, setThemePrefState] = useState<ThemePref>(cached.theme);
  const [prefersDark, setPrefersDark] = useState<boolean>(() => !!darkQuery()?.matches);
  const [view, setView] = useState<AppearanceView | null>(null);

  // The workspace in the URL (/:slug/...); non-workspace routes match no slug.
  const slug = location.pathname.split('/').filter(Boolean)[0];
  const tenant = useMemo(() => tenants.find((t) => t.slug === slug) ?? null, [tenants, slug]);
  const tenantId = tenant?.id ?? null;

  // Bumped to refetch (a change event, or after saving a system look while a workspace is open).
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!token) return;
    let current = true; // a workspace switch mid-request must not apply the old workspace's look
    appearanceService
      .get(apiFetch, tenantId)
      .then((v) => {
        if (current) setView(v);
      })
      .catch(() => {
        // Keep whatever is showing; the look is cosmetic and the next change event retries.
      });
    return () => {
      current = false;
    };
  }, [token, apiFetch, tenantId, tick]);

  useSocketEvent(SocketEvents.APPEARANCE_CHANGED, refresh);

  useEffect(() => {
    const q = darkQuery();
    if (!q) return;
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    q.addEventListener('change', onChange);
    return () => q.removeEventListener('change', onChange);
  }, []);

  const look = view?.look ?? cached.look;

  useEffect(() => {
    ensureLookFont(document, look);
    applyAppearance(document.documentElement, look, resolveTheme(themePref, prefersDark));
    writeCached(storage(), { look, theme: themePref });
  }, [look, themePref, prefersDark]);

  const setSystemLook = useCallback(
    async (next: Look) => {
      const v = await appearanceService.setSystem(apiFetch, next);
      // The answer is for "no workspace"; refetch so a workspace's own look stays in force.
      if (tenantId) refresh();
      else setView(v);
    },
    [apiFetch, tenantId, refresh],
  );

  const setTenantLook = useCallback(
    async (next: Look | null) => {
      if (!tenantId) return;
      setView(await appearanceService.setTenant(apiFetch, tenantId, next));
    },
    [apiFetch, tenantId],
  );

  const value = useMemo<AppearanceContextValue>(
    () => ({ look, themePref, setThemePref: setThemePrefState, view, tenant, setSystemLook, setTenantLook }),
    [look, themePref, view, tenant, setSystemLook, setTenantLook],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error('useAppearance must be used inside AppearanceProvider');
  return ctx;
}
